import React from 'react';

import { IconScout, type IconScoutName } from '@/components/ui/images/IconScout';
import { Text, type TextVariant } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';


export type BannerVariant = 'info' | 'warning' | 'error' | 'success';

/**
 * `comfortable` es el layout por defecto. `compact` reduce el espaciado y usa la
 * tipografía de cuerpo — pensado para banners de bloqueo al tope de una pantalla.
 */
export type BannerDensity = 'comfortable' | 'compact';

/** Segmento de texto del banner; `emphasis` lo renderiza en negrita. */
export interface BannerPart {
  text: string;
  emphasis: boolean;
}

export interface BannerProps {
  variant?: BannerVariant;
  /** Texto plano o segmentos con énfasis cuando el mensaje combina normal y negrita. */
  text: string | BannerPart[];
  icon?: IconScoutName;
  /** Sobrescribe el color calculado por `variant`/`density` para el icono y el botón de cierre. */
  iconColor?: string;
  /** Sobrescribe el color de fondo calculado por `variant`. */
  background?: string;
  /** Sobrescribe el `TextVariant` (tipografía/tamaño) calculado por `density`. */
  textVariant?: TextVariant;
  density?: BannerDensity;
  /** Cuando se entrega, muestra el botón de cierre a la derecha; requiere `dismissLabel`. */
  onDismiss?: () => void;
  dismissLabel?: string;
  className?: string;
  /** false para banners full-bleed (ej. bajo PageLayout) sin esquinas redondeadas. */
  rounded?: boolean;
  'data-testid'?: string;
}

interface BannerVariantConfig {
  background: string;
  iconColor: string;
  textColor: string;
  icon: IconScoutName;
}

const VARIANT_CONFIG: Record<BannerVariant, BannerVariantConfig> = {
  info: {
    background: colors.notification.primaryBackground,
    iconColor: colors.important.main,
    textColor: colors.primary.main,
    icon: 'info',
  },
  warning: {
    background: colors.notification.alertBackground,
    iconColor: colors.feedback.alert400,
    textColor: colors.grays.neutral33,
    icon: 'alert',
  },
  error: {
    background: colors.notification.errorBackground,
    iconColor: colors.feedback.error300,
    textColor: colors.grays.neutral33,
    icon: 'alert',
  },
  success: {
    background: colors.notification.successBackground,
    iconColor: colors.feedback.success400,
    textColor: colors.feedback.success400,
    icon: 'checkCircle',
  },
};

interface BannerDensityConfig {
  container: string;
  radius: string;
  textVariant: 'subheader-regular' | 'body-regular';
  iconSize: 'md' | number;
  /**
   * `compact` usa el tono de icono de `notification.*IconContainer` (más claro que el
   * `feedback.*400` de `comfortable`) para no alterar los banners de bloqueo ya diseñados.
   */
  iconColorByVariant?: Partial<Record<BannerVariant, string>>;
}

const DENSITY_CONFIG: Record<BannerDensity, BannerDensityConfig> = {
  comfortable: {
    container: 'gap-3 px-4 py-3',
    radius: 'rounded-lg',
    textVariant: 'subheader-regular',
    iconSize: 'md',
  },
  compact: {
    container: 'gap-2 px-4 py-3',
    radius: 'rounded-md',
    textVariant: 'body-regular',
    iconSize: 20,
    iconColorByVariant: {
      warning: colors.notification.alertIconContainer,
      error: colors.notification.errorIconContainer,
    },
  },
};

const renderText = (text: string | BannerPart[]): React.ReactNode => {
  if (typeof text === 'string') return text;

  return text.map((part, index) => (
    part.emphasis
      ? <strong key={index}>{part.text}</strong>
      : <React.Fragment key={index}>{part.text}</React.Fragment>
  ));
};

/**
 * Banner inline persistente (icono + texto) — NO es el toast flotante `Notification`.
 * Se usa para avisos que deben permanecer visibles mientras la condición se mantenga
 * (por ejemplo "este registro es de solo lectura" o un bloqueo de pantalla).
 */
export const Banner: React.FC<BannerProps> = ({
  variant = 'warning',
  text,
  icon,
  iconColor,
  background,
  textVariant,
  density = 'comfortable',
  onDismiss,
  dismissLabel,
  className = '',
  rounded = true,
  'data-testid': dataTestId,
}) => {
  const config = VARIANT_CONFIG[variant];
  const densityConfig = DENSITY_CONFIG[density];
  const resolvedIcon = icon ?? config.icon;
  const resolvedIconColor = iconColor
    ?? densityConfig.iconColorByVariant?.[variant]
    ?? config.iconColor;
  const resolvedBackground = background ?? config.background;
  const resolvedTextVariant = textVariant ?? densityConfig.textVariant;
  const resolvedTestId = dataTestId ?? 'banner';

  return (
    <div
      role="alert"
      className={`flex items-center ${densityConfig.container} ${rounded ? densityConfig.radius : ''} ${className}`}
      style={{ backgroundColor: resolvedBackground }}
      data-testid={resolvedTestId}
    >
      <IconScout
        name={resolvedIcon}
        size={densityConfig.iconSize}
        color={resolvedIconColor}
        data-testid="banner-icon"
      />
      <Text
        variant={resolvedTextVariant}
        color={config.textColor}
        className={onDismiss ? 'flex-1' : undefined}
      >
        {renderText(text)}
      </Text>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="p-1 rounded hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
          data-testid={`${resolvedTestId}-dismiss`}
        >
          <IconScout name="close" color={resolvedIconColor} size={14} />
        </button>
      )}
    </div>
  );
};

export default Banner;
